import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import type { ReactNode } from "react";

const EditInput = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: theme.palette.background.paper,
    "& fieldset": {
      borderColor: theme.palette.common.black,
    },
    "&:hover fieldset": {
      borderColor: theme.palette.common.black,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.common.black,
    },
  },
}));

type MessageEditFieldProps = {
  text: string;
  /** A write is in flight: Save and Cancel wait for it. */
  saving?: boolean;
  /** Drawn under the box: the proposal pane puts a comment's audience switch here. */
  helperText?: ReactNode;
  /** Names what Save saves: "Save comment" on a proposal, "Save message" on a work agreement. */
  saveLabel?: string;
  className?: string;
  onChange: (text: string) => void;
  onSave: () => void;
  onCancel: () => void;
};

/**
 * The box an author rewrites their own message in, with Save and Cancel. Enter saves and Escape cancels.
 *
 * The proposal pane's comment rows and the work agreement pane's messages both edit in this box, so an
 * edit looks and answers the keyboard the same in both.
 */
export default function MessageEditField(props: Readonly<MessageEditFieldProps>) {
  const { text, saving, helperText, className, onChange, onSave, onCancel } = props;
  const saveLabel = props.saveLabel ?? "Save comment";
  const empty = text.trim() === "";

  return (
    <EditInput
      className={className}
      multiline
      rows={4}
      variant="outlined"
      fullWidth
      value={text}
      helperText={helperText}
      slotProps={{
        input: {
          endAdornment: (
            <>
              <Tooltip title={saveLabel}>
                <IconButton aria-label={saveLabel} color="primary" size="small" disabled={empty || saving} onClick={onSave}>
                  <SaveIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel editing">
                <IconButton aria-label="Cancel editing" color="primary" size="small" disabled={saving} onClick={onCancel}>
                  <CancelIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </>
          ),
        },
      }}
      onKeyDown={(event) => {
        if (event.key === KeyboardKey.Enter && !empty) {
          event.preventDefault();
          // Enter is the same act as pressing Save, so it commits and closes the box.
          onSave();
        } else if (event.key === KeyboardKey.Escape) {
          onCancel();
        }
      }}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}
